/**
 * Shared Gag Breakdown page-state shapes.
 *
 * Extracted from GagBreakdown.js so `GagSavedViews` — which schema-validated
 * persisted view presets against these shapes — did not have to import the
 * page, which would have been a circular import.
 *
 * 🔻 **THAT CONSUMER IS GONE: the saved-view feature was deleted on
 * 2026-09-22 by owner ruling.** The shapes below are still the page's own, so
 * the module keeps its job; but `GROUP_MODES`, `FLAT_SORT_IDS` and
 * `FLAT_SORT_DIRS` were its WHITELISTS and now have no production importer at
 * all. They are still exported and still covered by `gagBreakdown.test.js`.
 * ⚠️ Removing three public exports is a different change from removing a
 * feature, so it is REPORTED AS OWED WORK rather than done here — and this
 * paragraph is what stops the next reader concluding they were simply missed.
 * 🔻 The filter model (`DEFAULT_FILTERS` and everything behind `Filters ▾`) is
 * deleted too (2026-10-02) — see the block above the status tabs.
 */

import { GAG_STATUSES, GAG_STATUS_LABELS } from './taskStatuses';

/** Group-by modes accepted by the breakdown table ('all' = flat, no groups). */
export const GROUP_MODES = ['all', 'sub_department', 'shoot_week', 'scene', 'assignment'];

/**
 * Sort options for the breakdown table. Historically these were the
 * flat-mode ("Group by: All") sort-select options only, and were persisted to a
 * localStorage key of their own until 2026-09-22 (deleted by owner ruling — the
 * page opens on its defaults every time);
 * they now double as the id/label set shared with the clickable column
 * headers — the Sort select and every sortable header drive ONE sort state
 * (column + direction). Sorting is client-side over the fetched list, applied
 * in flat mode AND within each group of the grouped modes.
 *
 * Existing ids (shoot/title/priority/status/sub_department) are preserved so
 * saved views + persisted prefs stored before the expansion still validated.
 * 🔻 Both of those readers are gone (2026-09-22); the ids are kept because
 * they are the SORT COLUMN vocabulary, which nothing about that deletion moves.
 */
export const FLAT_SORTS = [
  { id: 'title', label: 'Title' },
  { id: 'status', label: 'Status' },
  { id: 'ra', label: 'RA status' },
  { id: 'sub_department', label: 'Sub-department' },
  { id: 'scene', label: 'Scene' },
  // ⚖️ The page's DEFAULT sort, and the one column whose blanks sort FIRST
  // (owner ruling 2026-09-22: "no shoot date → past → today → tomorrow → …").
  // This comment read "NULLs last" until then; the comparator lives in
  // `GagGroupTable.js` (`nullsFirst`), which carries the reasoning.
  { id: 'shoot', label: 'Shoot date' }, // default — NULLs FIRST, then ascending
  { id: 'unit', label: 'Unit' },
  // 'effect' intentionally omitted — the Effect table column is hidden for now
  // (the comparator is kept in GagGroupTable for a trivial re-add).
  { id: 'day_night', label: 'D/N' },
  { id: 'int_ext', label: 'INT/EXT' },
  { id: 'location', label: 'Location' },
  { id: 'owner', label: 'Owner' },
  { id: 'priority', label: 'Priority' }, // urgent first
  { id: 'progress', label: 'Progress' },
];
export const FLAT_SORT_IDS = FLAT_SORTS.map((s) => s.id);

/**
 * Sort directions + defaults. 🔻 The direction was PERSISTED until 2026-09-22;
 * this page now stores none of its view state (owner ruling), so these two
 * defaults are what every visit opens on rather than a first-visit fallback —
 * which is what makes `DEFAULT_FLAT_SORT` a statement about the FIRST SCREEN.
 * 🔻 `FLAT_SORT_DIRS` and `FLAT_SORT_IDS` were `GagSavedViews`' whitelists and
 * that component is deleted too — see the module docblock above.
 */
export const FLAT_SORT_DIRS = ['asc', 'desc'];
export const DEFAULT_FLAT_SORT = 'shoot';
export const DEFAULT_FLAT_SORT_DIR = 'asc';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔻 THE FILTERS ▾ MODEL IS DELETED (owner ruling 2026-10-02) — at EVERY width
 * ═══════════════════════════════════════════════════════════════════════════
 * `DEFAULT_FILTERS`, the owner-selection model (`OWNER_SCOPE_*`,
 * `ownerUserToken`, `normaliseOwnerSelection`, `ownerSelectionToQuery`,
 * `ownerTokenLabel`), `activeFilterCount`, `GAG_FILTER_GROUPS`,
 * `gagFilterGroups`, `filterGroupCount` and `describeActiveFilters` all lived
 * here. The owner removed `Filters ▾` from the Breakdown outright: the status
 * TABS below, `Group ▾` and the column-header sort replace every filter group
 * (sub-department, priority, status, owner, shoot date, RA required), and
 * "Show deleted gags" became the `Deleted` tab. The search box is the only
 * other narrowing left, and it is page state (`GagBreakdown.js`), not a model.
 * **Do not reintroduce a filter shape here "for later"** — pre-deletion SHA
 * `cdbda68` holds the whole model, one `git show` away.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE STATUS TABS (owner rulings 2026-10-02) — every width, Labour excepted
 * ═══════════════════════════════════════════════════════════════════════════
 * `All · Not started · In progress · Blocked · Ready to shoot · Done ·
 * Cancelled · Deleted`, rendered by the shared `components/ui/Tabs` (desktop:
 * a row inside the sticky toolbar; phone: beside the search magnifier, via
 * `components/MobileTabSearchRow`). The status ids ARE the stored
 * `gags.status` values, so a tab never needs a mapping table; labels are
 * `GAG_STATUS_LABELS`, so a tab and the badge on the rows under it can never
 * say different words.
 *
 * 🔑 `All` is every LIVE gag — it excludes soft-deleted ones, exactly as the
 * list always has. `Deleted` is LAST and is not a status: it is the
 * soft-deleted set (any status), fetched separately (`?deleted=1`), with the
 * existing restore / permanent-delete actions. It replaced the "Show deleted
 * gags" checkbox and is offered wherever that checkbox was (everyone, every
 * view but Labour) — the server's `deleted=1` read is ungated, and the tab
 * resolves nothing new.
 * 🔑 The tab is SESSION state seeded from `DEFAULT_GAG_STATUS_TAB` — never
 * stored (the page persists no view state; see `GagBreakdown.js`).
 */
export const GAG_STATUS_TAB_ALL = 'all';
export const GAG_STATUS_TAB_DELETED = 'deleted';
export const DEFAULT_GAG_STATUS_TAB = GAG_STATUS_TAB_ALL;
export const GAG_STATUS_TABS = [
  { id: GAG_STATUS_TAB_ALL, label: 'All' },
  ...GAG_STATUSES.map((s) => ({ id: s, label: GAG_STATUS_LABELS[s] })),
  { id: GAG_STATUS_TAB_DELETED, label: 'Deleted' },
];

/**
 * The rows one tab shows. `liveGags` is the live list, `deletedGags` the
 * soft-deleted one; `Deleted` reads only the second, every other tab only the
 * first. An unknown id shows nothing, never everything.
 */
export function gagsInStatusTab(liveGags, tab, deletedGags = []) {
  if (tab === GAG_STATUS_TAB_DELETED) return deletedGags;
  if (tab === GAG_STATUS_TAB_ALL) return liveGags;
  return liveGags.filter((g) => g.status === tab);
}

/**
 * `{ all, todo, in_progress, …, deleted }` — each tab's count is exactly the
 * number of rows that tab renders (`gagsInStatusTab`). Every key is present,
 * `0` included (`ui/Tabs` renders a real `0`). A live row whose status is not a
 * known gag status counts toward `all` only. `deletedGags` of `null` means
 * "not known yet" and gives `deleted: null`, which `ui/Tabs` renders as NO
 * number rather than a false `0`.
 */
export function gagStatusTabCounts(liveGags, deletedGags = null) {
  const counts = { [GAG_STATUS_TAB_ALL]: liveGags.length };
  GAG_STATUSES.forEach((s) => { counts[s] = 0; });
  liveGags.forEach((g) => {
    if (GAG_STATUSES.includes(g.status)) counts[g.status] += 1;
  });
  counts[GAG_STATUS_TAB_DELETED] = Array.isArray(deletedGags) ? deletedGags.length : null;
  return counts;
}
